import React from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import { BannerFeedback } from "../BannerFeedback";
import { Button } from "../Button";
import { formatBRL } from "../financial/InvoiceSplitPreviewModal";
import type {
  CancellationEligibility,
  PixProcessingFee,
  WeeklyLimit,
} from "../../services/pixAutomatico";
import {
  canStartFrom,
  CANCEL_OUTSTANDING_SPEND,
  CANCEL_ELIGIBILITY_UNKNOWN,
  centsToReaisInput,
  DEFAULT_FIXED_WEEKLY_LIMIT_CENTS,
  parseReaisToCents,
  shouldPoll,
  type PixAutomaticoNotice,
  type PixAutomaticoView,
  type StoredWeeklyLimit,
} from "./pixAutomaticoState";
import "./PixAutomaticoPanel.css";

export type PixAutomaticoPanelProps = {
  view: PixAutomaticoView;
  /** Current weekly limit; null while the company has no valid stored policy. */
  weeklyLimit: StoredWeeklyLimit | null;
  /** `org_payments` create: start an authorization. */
  canCreate: boolean;
  /** `org_payments` edit: change the weekly limit. */
  canEdit: boolean;
  /** `org_payments` delete: cancel an authorization. */
  canDelete: boolean;
  /** Live financial decision; absent or unknown never offers local cancellation. */
  cancellationEligibility?: CancellationEligibility;
  /**
   * Payments accepts new authorizations. While it does not, an existing
   * authorization is still shown (and can be cancelled), and no start is
   * offered.
   */
  activationEnabled: boolean;
  /**
   * Payments' weekly admission is active, the only place the limit is
   * enforced. While it is not, the copy says the choice is only stored and
   * that cancelling pauses no campaign.
   */
  weeklyAdmissionActive: boolean;
  /** Terms version the company accepts; must match Payments. */
  termsVersion: string;
  /**
   * /termos publishes the billing conditions of `termsVersion`. While it does
   * not, no authorization is offered: Payments would record the acceptance of
   * a version the company cannot read.
   */
  termsPublished: boolean;
  /**
   * The Pix processing fee Payments reports (null before its first answer).
   * The panel shows only this amount, never one of its own, and offers a
   * start only while it is `reviewed`.
   */
  processingFee: PixProcessingFee | null;
  minimumChargeCents?: number;
  /** Server reason; the panel never derives exposure readiness from the cap. */
  limitBelowMinimum?: boolean;
  notice?: PixAutomaticoNotice | null;
  /** A poll failed and another one is scheduled. */
  pollingIssue?: boolean;
  starting?: boolean;
  cancelling?: boolean;
  savingLimit?: boolean;
  /** Initial state of the uncontrolled terms checkbox. */
  defaultTermsAccepted?: boolean;
  /** Opens with the weekly-limit editor expanded. */
  defaultEditingLimit?: boolean;
  /** Opens with the cancellation confirmation expanded. */
  defaultConfirmingCancel?: boolean;
  onStart: (weeklyLimit: WeeklyLimit) => void;
  onCancel: () => void;
  /** Resolves true when the weekly limit was saved, which closes the editor. */
  onSaveWeeklyLimit: (weeklyLimit: WeeklyLimit) => Promise<boolean> | boolean | void;
  onRetry: () => void;
  onDismissNotice?: () => void;
};

type Tone = "success" | "warning" | "danger" | "info" | "neutral";

const BADGE: Record<PixAutomaticoView["kind"], { label: string; tone: Tone } | null> = {
  loading: null,
  load_error: { label: "Indisponível", tone: "danger" },
  activation_disabled: { label: "Em ativação", tone: "info" },
  not_enrolled: { label: "Não autorizado", tone: "neutral" },
  profile_incomplete: { label: "Cadastro pendente", tone: "warning" },
  preparing: { label: "Preparando", tone: "info" },
  waiting: { label: "Aguardando aprovação", tone: "warning" },
  confirming: { label: "Confirmando", tone: "info" },
  reconciling: { label: "Em verificação", tone: "warning" },
  approved: { label: "Autorizado", tone: "success" },
  approved_unconfirmed: { label: "Confirmação pendente", tone: "warning" },
  cancel_requested: { label: "Cancelando", tone: "warning" },
  rejected: { label: "Recusado", tone: "danger" },
  expired: { label: "Expirado", tone: "neutral" },
  cancelled: { label: "Cancelado", tone: "neutral" },
  reviewed_retry: { label: "Pedido encerrado", tone: "neutral" },
  blocked: { label: "Em verificação", tone: "warning" },
};

const TONE_CLASS: Record<Tone, string> = {
  success: "pixAutomaticoBadgeSuccess",
  warning: "pixAutomaticoBadgeWarning",
  danger: "pixAutomaticoBadgeDanger",
  info: "pixAutomaticoBadgeInfo",
  neutral: "pixAutomaticoBadgeNeutral",
};

const FORECAST =
  "Quando os serviços líquidos acumulados atingem R$ 1,00, antes da tarifa, a cobrança pode ser preparada com data prevista para quarta-feira. A data depende do processamento do banco.";

/**
 * Screens of an authorization that still exists while Payments' activation is
 * off. Payments keeps reading and cancelling it (store.go records a
 * cancellation even while dormant), so the screen stays and says why nothing
 * new can start.
 */
const LIVE_DURING_ACTIVATION: ReadonlySet<PixAutomaticoView["kind"]> = new Set<
  PixAutomaticoView["kind"]
>(["preparing", "waiting", "confirming", "reconciling", "approved", "approved_unconfirmed"]);

/**
 * What cancelling an approved authorization does, as Payments implements it:
 * no new weekly charge is scheduled ("stop future scheduling"), and the open
 * week closes as a recovery invoice that stays owed instead of being debited.
 * It promises nothing about the panel gates: they follow Enterprise's
 * paymentMethodReady, which Payments publishes on its own terms. And while
 * Payments' weekly admission is not active nothing pauses a campaign, which
 * the copy says (CANCEL_PAUSES_NOTHING).
 */
const CANCEL_APPROVED =
  "Ao cancelar, a DaKasa deixa de agendar novas cobranças semanais pelo Pix Automático. O que suas campanhas já consumiram, inclusive na semana em andamento, continua devido.";

/** Said wherever cancelling is discussed while Payments' weekly admission is off. */
const CANCEL_PAUSES_NOTHING = "Cancelar não pausa as campanhas que estão no ar.";

function cancelApprovedCopy(weeklyAdmissionActive: boolean): string {
  return weeklyAdmissionActive ? CANCEL_APPROVED : `${CANCEL_APPROVED} ${CANCEL_PAUSES_NOTHING}`;
}

const checkboxSX = {
  color: "var(--neutral-11)",
  "&.Mui-checked": { color: "var(--brand-9)" },
};

const checkboxLabelSX = {
  alignItems: "flex-start",
  marginLeft: 0,
  "& .MuiFormControlLabel-label": {
    fontSize: "13px",
    color: "var(--neutral-11)",
    paddingTop: "var(--spacing-md)",
  },
};

function formatObservedAt(iso?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Spinner() {
  return <CircularProgress size={16} thickness={5} sx={{ color: "var(--brand-9)" }} />;
}

function Live({ children }: { children: React.ReactNode }) {
  return (
    <p className="pixAutomaticoLive" role="status">
      <Spinner />
      <span>{children}</span>
    </p>
  );
}

/**
 * The processing fee sentence, with the amount Payments reported. A fee
 * without a reviewed contract has no amount to show and says so instead of
 * showing a number.
 */
function FeeNote({ fee }: { fee: PixProcessingFee | null }) {
  if (!fee) return null;
  switch (fee.state) {
    case "reviewed":
      return (
        <p className="pixAutomaticoText" data-testid="pix-automatico-fee">
          A tarifa de processamento Pix de{" "}
          <strong data-testid="pix-automatico-fee-amount">
            {formatBRL(fee.centsPerWeeklyCharge)}
          </strong>
          , paga pela empresa, é incluída uma única vez na preparação da cobrança, antes do envio.
        </p>
      );
    case "unconfigured":
      return (
        <p className="pixAutomaticoHint" data-testid="pix-automatico-fee">
          A tarifa de processamento Pix de cada cobrança semanal ainda não foi definida.
        </p>
      );
    // Exhaustive over PixProcessingFee["state"].
    // no default
  }
}

/**
 * Explains the selected DaKasa limit without presenting it as a charge. The
 * bank remains authoritative for its own account limits in either mode.
 */
function weeklyLimitMeaning(limit: WeeklyLimit, fee: PixProcessingFee | null): string {
  const charge =
    fee?.state === "reviewed"
      ? `os serviços acumulados, após créditos elegíveis, mais a tarifa fixa de ${formatBRL(fee.centsPerWeeklyCharge)}, incluída somente na preparação`
      : "os serviços acumulados, após créditos elegíveis, mais a tarifa de processamento Pix vigente, incluída somente na preparação";
  if (limit.mode === "unlimited") {
    return `Sem teto semanal remove apenas o limite interno da DaKasa. Abaixo de R$ 1,00 líquido de serviços, antes da tarifa, os custos ficam para uma próxima semana. A cobrança continua sendo ${charge}; o banco da empresa ainda pode impor limites próprios ou recusar o débito.`;
  }
  return `O teto é só o máximo semanal da DaKasa, já incluindo serviços da semana, custos trazidos de semanas anteriores e tarifa. Ele não é um débito, uma reserva nem um valor mínimo. Abaixo de R$ 1,00 líquido de serviços, antes da tarifa, os custos ficam para uma próxima semana; a cobrança continua sendo ${charge}, limitada a ${formatBRL(limit.cents)}. O banco da empresa ainda pode impor limites próprios ou recusar o débito.`;
}

function storedLimitValue(limit: StoredWeeklyLimit): WeeklyLimit {
  return limit.mode === "fixed" ? { mode: "fixed", cents: limit.cents } : { mode: "unlimited" };
}

function limitFromDraft(mode: WeeklyLimit["mode"], draft: string): WeeklyLimit | null {
  if (mode === "unlimited") return { mode: "unlimited" };
  const cents = parseReaisToCents(draft);
  return cents === null ? null : { mode: "fixed", cents };
}

/**
 * What still has to happen before a start is offered, as one sentence: the
 * terms at the configured version published on /termos, a reviewed processing
 * fee reported by Payments, or both.
 */
function startPendingSentence(termsPublished: boolean, feeReviewed: boolean): string {
  const pending: string[] = [];
  if (!termsPublished) {
    pending.push("as condições de cobrança do Pix Automático forem publicadas nos Termos de Uso");
  }
  if (!feeReviewed) {
    pending.push("a tarifa de processamento Pix de cada cobrança semanal estiver definida");
  }
  return `A autorização fica disponível aqui quando ${pending.join(" e ")}.`;
}

function WeeklyLimitPicker(props: {
  mode: WeeklyLimit["mode"];
  draft: string;
  processingFee: PixProcessingFee | null;
  testIdPrefix: string;
  onModeChange: (mode: WeeklyLimit["mode"]) => void;
  onDraftChange: (draft: string) => void;
}) {
  const fieldId = React.useId();
  const selected = limitFromDraft(props.mode, props.draft);
  const invalid = props.mode === "fixed" && props.draft.trim() !== "" && selected === null;
  const describedBy = `${fieldId}-meaning`;

  return (
    <fieldset className="pixAutomaticoLimitOptions">
      <legend>Limite semanal da DaKasa</legend>
      <FormControlLabel
        sx={checkboxLabelSX}
        control={
          <Radio
            sx={checkboxSX}
            checked={props.mode === "fixed"}
            onChange={() => props.onModeChange("fixed")}
            name={`${fieldId}-mode`}
          />
        }
        label="Com teto semanal"
      />
      {props.mode === "fixed" && (
        <div className="pixAutomaticoField pixAutomaticoLimitAmount">
          <label htmlFor={fieldId}>Teto semanal (R$)</label>
          <input
            id={fieldId}
            data-testid={`${props.testIdPrefix}-input`}
            inputMode="decimal"
            autoComplete="off"
            placeholder="25.000,00"
            value={props.draft}
            onChange={(event) => props.onDraftChange(event.target.value)}
            aria-invalid={invalid}
            aria-describedby={describedBy}
          />
          {invalid && (
            <span className="pixAutomaticoFieldError" role="alert">
              Informe um valor maior que zero, como 25.000,00.
            </span>
          )}
        </div>
      )}
      <FormControlLabel
        sx={checkboxLabelSX}
        control={
          <Radio
            sx={checkboxSX}
            checked={props.mode === "unlimited"}
            onChange={() => props.onModeChange("unlimited")}
            name={`${fieldId}-mode`}
          />
        }
        label="Sem teto semanal"
      />
      <p
        id={describedBy}
        className="pixAutomaticoHint"
        data-testid={`${props.testIdPrefix}-meaning`}
      >
        {weeklyLimitMeaning(
          selected ?? { mode: "fixed", cents: DEFAULT_FIXED_WEEKLY_LIMIT_CENTS },
          props.processingFee,
        )}
      </p>
    </fieldset>
  );
}

function StartForm(props: {
  weeklyLimit: StoredWeeklyLimit | null;
  activationEnabled: boolean;
  canCreate: boolean;
  termsVersion: string;
  termsPublished: boolean;
  processingFee: PixProcessingFee | null;
  starting: boolean;
  defaultTermsAccepted: boolean;
  cta: string;
  onStart: (weeklyLimit: WeeklyLimit) => void;
}) {
  const [mode, setMode] = React.useState<WeeklyLimit["mode"]>(props.weeklyLimit?.mode ?? "fixed");
  const [draft, setDraft] = React.useState(
    props.weeklyLimit?.mode === "fixed"
      ? centsToReaisInput(props.weeklyLimit.cents)
      : centsToReaisInput(DEFAULT_FIXED_WEEKLY_LIMIT_CENTS),
  );
  const [accepted, setAccepted] = React.useState(props.defaultTermsAccepted);

  if (!props.activationEnabled) {
    return (
      <p className="pixAutomaticoHint" data-testid="pix-automatico-start-activation">
        Uma nova autorização fica disponível aqui quando a DaKasa concluir a ativação do Pix
        Automático.
      </p>
    );
  }

  // Nothing can be accepted before /termos publishes the conditions, nor
  // before Payments reports the fee the company is about to authorize.
  const fee = props.processingFee?.state === "reviewed" ? props.processingFee : null;
  if (!props.termsPublished || fee === null) {
    return (
      <p className="pixAutomaticoHint" data-testid="pix-automatico-start-pending">
        {startPendingSentence(props.termsPublished, fee !== null)}
      </p>
    );
  }

  const feeNote = <FeeNote fee={fee} />;

  if (!props.canCreate) {
    return (
      <>
        {feeNote}
        <p className="pixAutomaticoHint">
          Peça a alguém com permissão para criar pagamentos na sua organização para autorizar o Pix
          Automático.
        </p>
      </>
    );
  }

  const selectedLimit = props.weeklyLimit
    ? storedLimitValue(props.weeklyLimit)
    : limitFromDraft(mode, draft);

  return (
    <div className="pixAutomaticoStart">
      {feeNote}
      {props.weeklyLimit ? (
        <>
          <p className="pixAutomaticoText">
            A autorização vai usar o limite semanal atual:{" "}
            <strong data-testid="pix-automatico-start-weekly-limit">
              {props.weeklyLimit.mode === "fixed"
                ? formatBRL(props.weeklyLimit.cents)
                : "sem teto interno da DaKasa"}
            </strong>
            .
          </p>
          <p className="pixAutomaticoHint">
            {weeklyLimitMeaning(storedLimitValue(props.weeklyLimit), props.processingFee)}
          </p>
        </>
      ) : (
        <WeeklyLimitPicker
          mode={mode}
          draft={draft}
          processingFee={props.processingFee}
          testIdPrefix="pix-automatico-start-weekly-limit"
          onModeChange={setMode}
          onDraftChange={setDraft}
        />
      )}
      <FormControlLabel
        sx={checkboxLabelSX}
        control={
          <Checkbox
            sx={checkboxSX}
            checked={accepted}
            onChange={(event) => setAccepted(event.target.checked)}
          />
        }
        label={
          <span>
            Li e aceito as condições de cobrança do Pix Automático publicadas nos{" "}
            <Link to="/termos" target="_blank" rel="noopener noreferrer">
              Termos de Uso
            </Link>{" "}
            (versão {props.termsVersion}).
          </span>
        }
      />
      <div className="pixAutomaticoActions">
        <Button
          callback={() => {
            if (selectedLimit !== null && accepted) props.onStart(selectedLimit);
          }}
          disabled={!accepted || selectedLimit === null}
          loading={{ state: props.starting, text: "Gerando o QR Code" }}
        >
          {props.cta}
        </Button>
      </div>
    </div>
  );
}

function CancelControl(props: {
  label: string;
  explanation: string;
  keepLabel: string;
  cancelling: boolean;
  defaultConfirming: boolean;
  onCancel: () => void;
}) {
  const [confirming, setConfirming] = React.useState(props.defaultConfirming);
  if (!confirming) {
    return (
      <div className="pixAutomaticoActions">
        <button
          type="button"
          className="pixAutomaticoSecondary"
          data-testid="pix-automatico-cancel"
          onClick={() => setConfirming(true)}
        >
          {props.label}
        </button>
      </div>
    );
  }
  return (
    <div className="pixAutomaticoConfirm" role="group" aria-label={props.label}>
      <p className="pixAutomaticoText">{props.explanation}</p>
      <div className="pixAutomaticoActions">
        <Button
          type="danger"
          callback={props.onCancel}
          loading={{ state: props.cancelling, text: "Cancelando" }}
        >
          Confirmar cancelamento
        </Button>
        <button
          type="button"
          className="pixAutomaticoSecondary"
          onClick={() => setConfirming(false)}
          disabled={props.cancelling}
        >
          {props.keepLabel}
        </button>
      </div>
    </div>
  );
}

function CopyAndPaste({ value }: { value: string }) {
  const fieldId = React.useId();
  const textRef = React.useRef<HTMLTextAreaElement>(null);
  const [copy, setCopy] = React.useState<"idle" | "copied" | "failed">("idle");
  const resetTimer = React.useRef<number | undefined>(undefined);

  React.useEffect(() => () => window.clearTimeout(resetTimer.current), []);

  async function copyCode() {
    window.clearTimeout(resetTimer.current);
    try {
      if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(value);
      setCopy("copied");
      resetTimer.current = window.setTimeout(() => setCopy("idle"), 3000);
    } catch {
      textRef.current?.select();
      setCopy("failed");
    }
  }

  return (
    <div className="pixAutomaticoField">
      <label htmlFor={fieldId}>Pix copia e cola</label>
      <textarea
        id={fieldId}
        ref={textRef}
        className="pixAutomaticoCode"
        data-testid="pix-automatico-copy-paste"
        readOnly
        rows={3}
        value={value}
        onFocus={(event) => event.currentTarget.select()}
      />
      <div className="pixAutomaticoActions">
        <Button callback={() => void copyCode()}>
          {copy === "copied" ? "Código copiado" : "Copiar código"}
        </Button>
      </div>
      {copy === "failed" && (
        <span className="pixAutomaticoFieldError" role="alert">
          Não foi possível copiar automaticamente. O código está selecionado: copie com o teclado.
        </span>
      )}
    </div>
  );
}

function WeeklyLimitSection(props: {
  weeklyLimit: StoredWeeklyLimit | null;
  processingFee: PixProcessingFee | null;
  weeklyAdmissionActive: boolean;
  canEdit: boolean;
  saving: boolean;
  defaultEditing: boolean;
  onSave: PixAutomaticoPanelProps["onSaveWeeklyLimit"];
}) {
  const [editing, setEditing] = React.useState(props.defaultEditing);
  const [mode, setMode] = React.useState<WeeklyLimit["mode"]>(props.weeklyLimit?.mode ?? "fixed");
  const [draft, setDraft] = React.useState(
    props.weeklyLimit?.mode === "fixed"
      ? centsToReaisInput(props.weeklyLimit.cents)
      : centsToReaisInput(DEFAULT_FIXED_WEEKLY_LIMIT_CENTS),
  );
  const selected = limitFromDraft(mode, draft);

  function resetEditor() {
    setMode(props.weeklyLimit?.mode ?? "fixed");
    setDraft(
      props.weeklyLimit?.mode === "fixed"
        ? centsToReaisInput(props.weeklyLimit.cents)
        : centsToReaisInput(DEFAULT_FIXED_WEEKLY_LIMIT_CENTS),
    );
  }

  function openEditor() {
    resetEditor();
    setEditing(true);
  }

  async function save() {
    if (selected === null) return;
    if ((await props.onSave(selected)) === true) setEditing(false);
  }

  const stored = props.weeklyLimit ? storedLimitValue(props.weeklyLimit) : null;

  return (
    <div className="pixAutomaticoLimit" data-testid="pix-automatico-weekly-limit">
      <div className="pixAutomaticoLimitHeader">
        <span className="pixAutomaticoLimitLabel">Limite semanal</span>
        {props.weeklyLimit ? (
          <strong
            className="pixAutomaticoLimitValue"
            data-testid="pix-automatico-weekly-limit-value"
          >
            {props.weeklyLimit.mode === "fixed"
              ? formatBRL(props.weeklyLimit.cents)
              : "Sem teto interno"}
          </strong>
        ) : (
          <span className="pixAutomaticoLimitEmpty">Nenhum limite definido</span>
        )}
        {props.canEdit && !editing && (
          <button
            type="button"
            className="pixAutomaticoSecondary"
            data-testid="pix-automatico-weekly-limit-edit"
            onClick={openEditor}
          >
            {props.weeklyLimit ? "Alterar limite" : "Definir limite"}
          </button>
        )}
      </div>
      {/* The full rule is long and the value above already answers "how much":
          it stays one tap away instead of filling the card. */}
      {stored && (
        <details className="pixAutomaticoExplainer">
          <summary>O que o limite significa</summary>
          <p className="pixAutomaticoHint" data-testid="pix-automatico-weekly-limit-meaning">
            {weeklyLimitMeaning(stored, props.processingFee)}
          </p>
        </details>
      )}
      {!props.weeklyAdmissionActive && (
        <p className="pixAutomaticoHint" data-testid="pix-automatico-weekly-limit-stored-only">
          Por enquanto esta escolha só fica registrada: nenhuma campanha é limitada ou pausada por
          ela ainda.
        </p>
      )}
      {/* A permission revoked while editing (live RTA update) closes the editor. */}
      {editing && props.canEdit ? (
        <div className="pixAutomaticoLimitEditor">
          <WeeklyLimitPicker
            mode={mode}
            draft={draft}
            processingFee={props.processingFee}
            testIdPrefix="pix-automatico-weekly-limit"
            onModeChange={setMode}
            onDraftChange={setDraft}
          />
          <div className="pixAutomaticoActions">
            <Button
              callback={() => void save()}
              disabled={selected === null}
              loading={{ state: props.saving, text: "Salvando" }}
            >
              Salvar limite
            </Button>
            <button
              type="button"
              className="pixAutomaticoSecondary"
              onClick={() => {
                resetEditor();
                setEditing(false);
              }}
              disabled={props.saving}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Portuguese names of the Enterprise profile fields (Enterprise ADR 0009). */
const PROFILE_FIELD_LABELS: Record<string, string> = {
  orgCNPJ: "CNPJ",
  orgName: "razão social",
  orgEmail: "e-mail",
  orgCountry: "país",
  orgState: "estado",
  orgCity: "cidade",
  orgZipCode: "CEP",
  orgStreet: "rua",
  orgNumberStreet: "número",
  orgNeighborhood: "bairro",
  orgPhoneCountry: "telefone da empresa",
  orgPhoneArea: "DDD do telefone",
  orgPhoneNumber: "telefone",
};

function profileFieldLabel(field: string): string {
  return PROFILE_FIELD_LABELS[field] ?? field;
}

function missingProfileLabels(fields: string[]): string[] {
  const phoneFields = ["orgPhoneCountry", "orgPhoneArea", "orgPhoneNumber"];
  const missingPhoneFields = fields.filter((field) => phoneFields.includes(field));
  if (missingPhoneFields.length < 2) return fields.map(profileFieldLabel);
  // The organization form has one DDD + number control and supplies +55 for
  // a complete Brazilian phone. Ask for that control once, not three pieces.
  const firstPhoneIndex = fields.findIndex((field) => phoneFields.includes(field));
  return fields
    .filter((field, index) => !phoneFields.includes(field) || index === firstPhoneIndex)
    .map((field) =>
      phoneFields.includes(field) ? "telefone da empresa" : profileFieldLabel(field),
    );
}

/**
 * Weekly Pix Automatico authorization for the payments page: explains the
 * weekly charge and the Pix processing fee Payments reports for it, shows the
 * recurrence QR and its Pix copia e cola while the bank approval is pending
 * (only next to a fee Payments reports as reviewed), and the final state with
 * the action each one allows. Purely presentational: `usePixAutomatico` owns
 * fetching and polling.
 *
 * The QR keeps qrcode.react's black on white in both themes on purpose: bank
 * scanners need dark modules on a light quiet zone, so it is content that must
 * not follow the palette (like the ad-builder canvas). Everything around it
 * uses the design tokens.
 *
 * @story layout padded
 * @story args { view: { kind: "not_enrolled" }, weeklyLimit: null, notice: null, activationEnabled: true, weeklyAdmissionActive: false, canCreate: true, canEdit: true, canDelete: true, termsVersion: "2026-10-v4", termsPublished: true, cancellationEligibility: { state: "allowed", reason: "no_outstanding_confirmed_spend" }, processingFee: { state: "reviewed", centsPerWeeklyCharge: 350 } }
 * @story state Loading { view: { kind: "loading" } }
 * @story state LoadError { view: { kind: "load_error", message: "Não foi possível carregar a cobrança semanal agora. Tente de novo em instantes." } }
 * @story state ActivationDisabled { view: { kind: "activation_disabled" }, activationEnabled: false }
 * @story state ApprovedWhileActivationOff { view: { kind: "approved", observedAt: "2026-09-23T12:00:00Z" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 }, activationEnabled: false }
 * @story state RejectedWhileActivationOff { view: { kind: "rejected" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 }, activationEnabled: false }
 * @story state NotEnrolledWithFixedLimit { view: { kind: "not_enrolled" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 2 }, defaultTermsAccepted: true }
 * @story state NotEnrolledUnlimited { view: { kind: "not_enrolled" }, weeklyLimit: { mode: "unlimited", version: 2 }, defaultTermsAccepted: true }
 * @story state NotEnrolledWithoutPermission { view: { kind: "not_enrolled" }, canCreate: false, canEdit: false, canDelete: false }
 * @story state TermsNotPublished { view: { kind: "not_enrolled" }, termsPublished: false }
 * @story state FeeUnconfigured { view: { kind: "not_enrolled" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 }, processingFee: { state: "unconfigured" } }
 * @story state TermsAndFeePending { view: { kind: "not_enrolled" }, termsPublished: false, processingFee: { state: "unconfigured" } }
 * @story state Starting { view: { kind: "not_enrolled" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 2 }, defaultTermsAccepted: true, starting: true }
 * @story state ProfileIncomplete { view: { kind: "profile_incomplete", missing: ["orgNeighborhood", "orgPhoneNumber"] }, weeklyLimit: { mode: "fixed", cents: 2500000, version: 1 } }
 * @story state ProfileRegistryUnavailable { view: { kind: "profile_incomplete", registryUnavailable: true }, weeklyLimit: { mode: "fixed", cents: 2500000, version: 1 } }
 * @story state ProfileOnItsWay { view: { kind: "profile_incomplete" }, weeklyLimit: { mode: "fixed", cents: 2500000, version: 1 } }
 * @story state Preparing { view: { kind: "preparing" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 } }
 * @story state WaitingForApproval { view: { kind: "waiting", pixCopyAndPaste: "00020101021226840014br.gov.bcb.pix2562pix.storybook.invalid/rec/demo-recorrencia-semanal5204000053039865802BR5906DAKASA6009SAO PAULO62070503***6304ABCD" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 } }
 * @story state WaitingFeeUnconfigured { view: { kind: "waiting", pixCopyAndPaste: "00020101021226840014br.gov.bcb.pix2562pix.storybook.invalid/rec/demo-recorrencia-semanal5204000053039865802BR5906DAKASA6009SAO PAULO62070503***6304ABCD" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 }, processingFee: { state: "unconfigured" } }
 * @story state WaitingWithPollingIssue { view: { kind: "waiting", pixCopyAndPaste: "00020101021226840014br.gov.bcb.pix2562pix.storybook.invalid/rec/demo-recorrencia-semanal5204000053039865802BR5906DAKASA6009SAO PAULO62070503***6304ABCD" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 }, pollingIssue: true }
 * @story state Confirming { view: { kind: "confirming" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 } }
 * @story state Reconciling { view: { kind: "reconciling" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 } }
 * @story state Approved { view: { kind: "approved", observedAt: "2026-09-23T12:00:00Z" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 } }
 * @story state ApprovedWithWeeklyAdmission { view: { kind: "approved", observedAt: "2026-09-23T12:00:00Z" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 }, weeklyAdmissionActive: true, defaultConfirmingCancel: true }
 * @story state ApprovedUnconfirmed { view: { kind: "approved_unconfirmed", observedAt: "2026-09-23T12:00:00Z" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 } }
 * @story state ApprovedFeeUnconfigured { view: { kind: "approved", observedAt: "2026-09-23T12:00:00Z" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 }, processingFee: { state: "unconfigured" } }
 * @story state ApprovedEditingLimit { view: { kind: "approved", observedAt: "2026-09-23T12:00:00Z" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 }, defaultEditingLimit: true }
 * @story state WeeklyLimitConflict { view: { kind: "approved", observedAt: "2026-09-23T12:00:00Z" }, weeklyLimit: { mode: "fixed", cents: 200000, version: 3 }, defaultEditingLimit: true, notice: { tone: "warning", message: "Outra pessoa alterou o limite semanal enquanto você editava. Carregamos a escolha atual: revise e salve de novo." } }
 * @story state CancellationBlocked { view: { kind: "approved" }, cancellationEligibility: { state: "blocked", reason: "confirmed_spend_outstanding" } }
 * @story state CancellationUnknown { view: { kind: "approved" }, cancellationEligibility: { state: "unknown", reason: "financial_authority_unavailable" } }
 * @story state ApprovedConfirmingCancel { view: { kind: "approved", observedAt: "2026-09-23T12:00:00Z" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 }, defaultConfirmingCancel: true }
 * @story state CancelRequested { view: { kind: "cancel_requested" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 } }
 * @story state Rejected { view: { kind: "rejected" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 } }
 * @story state Expired { view: { kind: "expired" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 } }
 * @story state Cancelled { view: { kind: "cancelled" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 } }
 * @story state Blocked { view: { kind: "blocked" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 } }
 * @story state ReviewedRetry { view: { kind: "reviewed_retry" }, weeklyLimit: { mode: "fixed", cents: 150000, version: 1 } }
 * @story token --neutral-2 --neutral-a5 --brand-9 --success-3 --warning-3 --danger-3 --info-3
 */
export function PixAutomaticoPanel(props: PixAutomaticoPanelProps) {
  const titleId = React.useId();
  const { view } = props;
  const badge = BADGE[view.kind];
  const moving = shouldPoll(view);
  const startable = canStartFrom(view) && props.activationEnabled;
  // The start form asks for the first weekly limit itself. When it cannot (no
  // create permission, terms not published yet, no reviewed fee, activation
  // off) the limit section does: Payments stores the explicit choice whatever
  // the activation says.
  const startAsksWeeklyLimit =
    startable &&
    !props.weeklyLimit &&
    props.canCreate &&
    props.termsPublished &&
    props.processingFee?.state === "reviewed";
  const showWeeklyLimit =
    view.kind !== "loading" && view.kind !== "load_error" && !startAsksWeeklyLimit;
  const activationNote = !props.activationEnabled && LIVE_DURING_ACTIVATION.has(view.kind);

  const cancellation = (label: string, explanation: string, keepLabel: string) => {
    if (!props.canDelete) return null;
    if (props.cancellationEligibility?.state !== "allowed") {
      return (
        <div className="pixAutomaticoActions" data-testid="pix-automatico-cancellation-unavailable">
          <p className="pixAutomaticoHint" role="status">
            {props.cancellationEligibility?.state === "blocked"
              ? CANCEL_OUTSTANDING_SPEND
              : CANCEL_ELIGIBILITY_UNKNOWN}
          </p>
          <p className="pixAutomaticoHint">
            Você pode cancelar a autorização no banco. O cancelamento não extingue os valores
            devidos.
          </p>
          {view.kind !== "preparing" && view.kind !== "approved_unconfirmed" && (
            <>
              <Link className="pixAutomaticoSecondary" to="/suporte">
                Falar com o suporte
              </Link>
              <button type="button" className="pixAutomaticoSecondary" onClick={props.onRetry}>
                Verificar de novo
              </button>
            </>
          )}
        </div>
      );
    }
    return (
      <CancelControl
        label={label}
        explanation={explanation}
        keepLabel={keepLabel}
        cancelling={props.cancelling === true}
        defaultConfirming={props.defaultConfirmingCancel === true}
        onCancel={props.onCancel}
      />
    );
  };

  const startForm = (cta: string) => (
    <StartForm
      weeklyLimit={props.weeklyLimit}
      activationEnabled={props.activationEnabled}
      canCreate={props.canCreate}
      termsVersion={props.termsVersion}
      termsPublished={props.termsPublished}
      processingFee={props.processingFee}
      starting={props.starting === true}
      defaultTermsAccepted={props.defaultTermsAccepted === true}
      cta={cta}
      onStart={props.onStart}
    />
  );

  function body(): React.ReactNode {
    switch (view.kind) {
      case "loading":
        return <Live>Carregando a cobrança semanal.</Live>;
      case "load_error":
        return (
          <>
            <p className="pixAutomaticoText" role="alert">
              {view.message}
            </p>
            <div className="pixAutomaticoActions">
              <Button callback={props.onRetry}>Tentar de novo</Button>
            </div>
          </>
        );
      case "activation_disabled":
        return (
          <>
            <p className="pixAutomaticoLead">O Pix Automático ainda está em ativação</p>
            <p className="pixAutomaticoText">
              A DaKasa está concluindo a ativação da cobrança semanal com o parceiro de pagamentos.
              Assim que ela terminar, você poderá autorizar por aqui.
            </p>
          </>
        );
      case "not_enrolled":
        return (
          <>
            <p className="pixAutomaticoLead">Autorize a cobrança semanal</p>
            <p className="pixAutomaticoText">
              A cada semana, a DaKasa cobra pelo Pix Automático o que suas campanhas consumiram.
              Você autoriza uma vez no app do banco da empresa, e autorizar não é um pagamento.
            </p>
            <p className="pixAutomaticoHint">{FORECAST}</p>
            {startForm("Gerar QR Code de autorização")}
          </>
        );
      // Payments needs the company fiscal profile before an authorization. The
      // controller already asked Enterprise to republish it (Enterprise ADR
      // 0009), so this screen appears only when a field the company must fill
      // in is missing, the company registry is down, or the profile is still
      // on its way.
      case "profile_incomplete": {
        const missing = view.missing ?? [];
        const needsSupport = missing.includes("orgCityCode");
        const editable = missing.filter((field) => field !== "orgCityCode");
        if (missing.length > 0) {
          return (
            <>
              <p className="pixAutomaticoLead">Faltam dados da empresa para a cobrança</p>
              {editable.length > 0 && (
                <p className="pixAutomaticoText">
                  Preencha nos dados da organização: {missingProfileLabels(editable).join(", ")}.
                  Depois é só gerar o QR Code de novo.
                </p>
              )}
              {needsSupport && (
                <p className="pixAutomaticoText">
                  O cadastro da Receita Federal não informou o município da empresa. Fale com o
                  suporte para concluirmos por você.
                </p>
              )}
              <div className="pixAutomaticoActions">
                {editable.length > 0 && (
                  <Link className="pixAutomaticoSecondary" to="/organizacao/info">
                    Completar dados da organização
                  </Link>
                )}
                {needsSupport && (
                  <Link className="pixAutomaticoSecondary" to="/suporte">
                    Falar com o suporte
                  </Link>
                )}
                <button type="button" className="pixAutomaticoSecondary" onClick={props.onRetry}>
                  Tentar de novo
                </button>
              </div>
            </>
          );
        }
        return (
          <>
            <p className="pixAutomaticoLead">
              {view.registryUnavailable
                ? "Não conseguimos confirmar o cadastro da empresa agora"
                : "Estamos concluindo o cadastro de cobrança"}
            </p>
            <p className="pixAutomaticoText">
              {view.registryUnavailable
                ? "A consulta ao cadastro da Receita Federal está indisponível no momento. Tente de novo em alguns minutos."
                : "O cadastro da empresa foi enviado para a cobrança e leva alguns segundos para chegar. Tente de novo em instantes."}
            </p>
            <div className="pixAutomaticoActions">
              <button type="button" className="pixAutomaticoSecondary" onClick={props.onRetry}>
                Tentar de novo
              </button>
            </div>
          </>
        );
      }
      case "preparing":
        return (
          <>
            <p className="pixAutomaticoLead">Preparando a autorização</p>
            <Live>
              Ainda não temos o QR Code ou o Pix copia e cola deste pedido. Esta tela consulta o
              andamento automaticamente.
            </Live>
            <p className="pixAutomaticoText">
              Se continuar assim, verifique de novo ou fale com o suporte. Não gere outra
              autorização enquanto este pedido estiver em andamento.
            </p>
            <div className="pixAutomaticoActions">
              <button type="button" className="pixAutomaticoSecondary" onClick={props.onRetry}>
                Verificar de novo
              </button>
              <Link className="pixAutomaticoSecondary" to="/suporte">
                Falar com o suporte
              </Link>
            </div>
            {cancellation(
              "Cancelar pedido",
              "Vamos registrar o cancelamento deste pedido. A confirmação pode depender do banco; até lá, não gere outra autorização.",
              "Voltar",
            )}
          </>
        );
      case "waiting": {
        const cancelRequest = cancellation(
          "Cancelar pedido",
          "Vamos pedir ao banco o cancelamento deste pedido de autorização.",
          "Voltar",
        );
        // Approving at the bank is how the company confirms the
        // authorization, so the QR and its code are shown only next to the
        // fee Payments reports as reviewed. The request can still be
        // cancelled, and polling still follows it.
        if (props.processingFee?.state !== "reviewed") {
          return (
            <>
              <p className="pixAutomaticoLead">A aprovação no banco aguarda a tarifa</p>
              <p className="pixAutomaticoText" data-testid="pix-automatico-qr-withheld">
                A tarifa de processamento Pix de cada cobrança semanal ainda não foi definida. O QR
                Code deste pedido só aparece aqui depois disso, para a empresa aprovar já sabendo o
                valor. Abra esta página de novo mais tarde.
              </p>
              {cancelRequest}
            </>
          );
        }
        return (
          <div className="pixAutomaticoWaiting">
            <div className="pixAutomaticoQr" data-testid="pix-automatico-qr">
              <QRCodeSVG
                value={view.pixCopyAndPaste}
                size={208}
                level="M"
                marginSize={4}
                title="QR Code da autorização do Pix Automático"
              />
            </div>
            <div className="pixAutomaticoWaitingText">
              <p className="pixAutomaticoLead">Aprove no app do banco da empresa</p>
              <ol className="pixAutomaticoSteps">
                <li>
                  Abra o app do banco e escolha pagar com Pix por QR Code ou Pix copia e cola.
                </li>
                <li>Escaneie o QR Code ou cole o código abaixo.</li>
                <li>Confira os dados da DaKasa e aprove a autorização do Pix Automático.</li>
              </ol>
              <FeeNote fee={props.processingFee} />
              <p className="pixAutomaticoHint">
                Aprovar não faz nenhum pagamento agora: só autoriza as cobranças semanais. A DaKasa
                cobra os serviços acumulados, após créditos elegíveis, mais a tarifa de
                processamento Pix na preparação. Abaixo de R$ 1,00 líquido de serviços, antes da
                tarifa, os custos ficam para uma próxima semana. O banco da empresa ainda pode impor
                limites próprios ou recusar o débito
                {props.weeklyLimit?.mode === "fixed"
                  ? `; se ele pedir um máximo por cobrança, um valor menor que o teto de ${formatBRL(
                      props.weeklyLimit.cents,
                    )} pode recusar uma semana que chegou a esse teto.`
                  : "."}
              </p>
              <CopyAndPaste value={view.pixCopyAndPaste} />
              <Live>Aguardando a aprovação no banco. Esta tela se atualiza sozinha.</Live>
              {cancelRequest}
            </div>
          </div>
        );
      }
      case "confirming":
        return (
          <>
            <p className="pixAutomaticoLead">Confirmando com o banco</p>
            <Live>
              Ainda não temos uma resposta clara do banco sobre esta autorização. Esta tela se
              atualiza sozinha.
            </Live>
          </>
        );
      case "reconciling":
        return (
          <>
            <p className="pixAutomaticoLead">Autorização em verificação</p>
            <p className="pixAutomaticoText">
              O pedido foi registrado, mas ainda não conseguimos confirmar se o banco criou a
              autorização. Não gere outro QR Code enquanto verificamos o resultado.
            </p>
            <Live>Esta tela será atualizada se o banco confirmar a autorização.</Live>
            <div className="pixAutomaticoActions">
              <Link className="pixAutomaticoSecondary" to="/suporte">
                Falar com o suporte
              </Link>
            </div>
          </>
        );
      case "approved": {
        const observed = formatObservedAt(view.observedAt);
        return (
          <>
            <p className="pixAutomaticoLead">A cobrança semanal está autorizada</p>
            <p className="pixAutomaticoText">
              O valor de cada semana é calculado com o consumo das suas campanhas e debitado pelo
              Pix Automático. {FORECAST}
            </p>
            <FeeNote fee={props.processingFee} />
            {observed && (
              <p className="pixAutomaticoHint">Última confirmação com o banco: {observed}.</p>
            )}
            {cancellation(
              "Cancelar autorização",
              cancelApprovedCopy(props.weeklyAdmissionActive),
              "Manter autorização",
            )}
          </>
        );
      }
      // Payments' readiness does not count the approval right now: most often
      // the last observation of the bank is older than its freshness window.
      case "approved_unconfirmed": {
        const observed = formatObservedAt(view.observedAt);
        return (
          <>
            <p className="pixAutomaticoLead">Autorização aprovada, aguardando confirmação</p>
            <p className="pixAutomaticoText">
              O banco aprovou esta autorização, mas a DaKasa não conseguiu confirmá-la com o banco
              agora, e enquanto isso ela não conta como pronta para a cobrança semanal. A DaKasa
              tenta de novo sozinha: verifique em alguns minutos e, se continuar assim, fale com o
              suporte.
            </p>
            <FeeNote fee={props.processingFee} />
            {observed && (
              <p className="pixAutomaticoHint">Última confirmação com o banco: {observed}.</p>
            )}
            <div className="pixAutomaticoActions">
              <Button callback={props.onRetry}>Verificar de novo</Button>
              <Link className="pixAutomaticoSecondary" to="/suporte">
                Falar com o suporte
              </Link>
            </div>
            {cancellation(
              "Cancelar autorização",
              cancelApprovedCopy(props.weeklyAdmissionActive),
              "Manter autorização",
            )}
          </>
        );
      }
      case "cancel_requested":
        return (
          <>
            <p className="pixAutomaticoLead">Cancelamento em andamento</p>
            <Live>
              Pedimos ao banco o cancelamento da autorização. Nenhuma cobrança nova é agendada
              enquanto isso, e esta tela se atualiza sozinha.
            </Live>
          </>
        );
      case "rejected":
        return (
          <>
            <p className="pixAutomaticoLead">O banco recusou a autorização</p>
            <p className="pixAutomaticoText">
              Nada foi cobrado por esta autorização. Se quiser, gere um novo QR Code e aprove de
              novo no app do banco.
            </p>
            {startForm("Gerar novo QR Code")}
          </>
        );
      case "expired":
        return (
          <>
            <p className="pixAutomaticoLead">O QR Code expirou</p>
            <p className="pixAutomaticoText">
              O prazo para aprovar terminou antes da aprovação no banco. Gere um novo QR Code para
              tentar de novo.
            </p>
            {startForm("Gerar novo QR Code")}
          </>
        );
      case "cancelled":
        return (
          <>
            <p className="pixAutomaticoLead">Autorização cancelada</p>
            <p className="pixAutomaticoText">
              Esta autorização não vale para novas cobranças semanais, e o consumo já registrado
              continua devido.{!props.weeklyAdmissionActive && ` ${CANCEL_PAUSES_NOTHING}`}
            </p>
            {startForm("Autorizar novamente")}
          </>
        );
      case "reviewed_retry":
        return (
          <>
            <p className="pixAutomaticoLead">O pedido anterior foi encerrado</p>
            <p className="pixAutomaticoText">
              Após verificar o resultado com o parceiro de pagamentos, encerramos esta tentativa.
              Ela não autorizou as cobranças semanais. Para continuar, gere um novo pedido e aprove
              no app do banco da empresa.
            </p>
            {startForm("Gerar novo QR Code")}
          </>
        );
      case "blocked":
        return (
          <>
            <p className="pixAutomaticoLead">Autorização em verificação</p>
            <p className="pixAutomaticoText">
              Não conseguimos confirmar com o banco a criação desta autorização, e a nossa equipe
              precisa conferir antes de continuar. Nenhuma cobrança é feita enquanto isso.
            </p>
            <div className="pixAutomaticoActions">
              <Link className="pixAutomaticoSecondary" to="/suporte">
                Falar com o suporte
              </Link>
            </div>
          </>
        );
      // Exhaustive over PixAutomaticoView["kind"].
      // no default
    }
  }

  return (
    <section
      className="pixAutomatico"
      data-testid="pix-automatico-panel"
      data-view={view.kind}
      aria-labelledby={titleId}
      aria-busy={view.kind === "loading"}
    >
      <header className="pixAutomaticoHeader">
        <div className="pixAutomaticoIdentity">
          <span className="pixAutomaticoIcon" aria-hidden="true">
            <EventRepeatOutlinedIcon fontSize="small" />
          </span>
          <div>
            <h2 className="pixAutomaticoTitle" id={titleId}>
              Pix Automático
            </h2>
            <p className="pixAutomaticoSubtitle">Forma de pagamento da cobrança semanal</p>
          </div>
        </div>
        {badge && (
          <span
            className={`pixAutomaticoBadge ${TONE_CLASS[badge.tone]}`}
            data-testid="pix-automatico-status"
          >
            {badge.label}
          </span>
        )}
      </header>

      {props.notice && (
        <BannerFeedback
          type={props.notice.tone}
          open
          message={props.notice.message}
          callback={props.onDismissNotice}
        />
      )}

      {activationNote && (
        <p className="pixAutomaticoHint" data-testid="pix-automatico-activation-note">
          A DaKasa está concluindo a ativação do Pix Automático. Esta autorização continua
          registrada enquanto isso.
        </p>
      )}

      {props.limitBelowMinimum && (
        <p className="pixAutomaticoHint" role="status" data-testid="pix-automatico-limit-minimum">
          O limite informado não comporta o mínimo líquido de serviços e a tarifa
          {props.minimumChargeCents !== undefined
            ? ` (${formatBRL(props.minimumChargeCents)} no total mínimo)`
            : ""}
          . Ajuste o limite para permitir novas admissões; uma autorização já aprovada permanece
          válida.
        </p>
      )}
      <div className="pixAutomaticoBody">{body()}</div>

      {moving && props.pollingIssue && (
        <p className="pixAutomaticoPollingIssue" role="status">
          Não conseguimos atualizar o estado agora. Vamos tentar de novo em instantes.
        </p>
      )}

      {showWeeklyLimit && (
        <WeeklyLimitSection
          key={props.weeklyLimit?.version ?? 0}
          weeklyLimit={props.weeklyLimit}
          processingFee={props.processingFee}
          weeklyAdmissionActive={props.weeklyAdmissionActive}
          canEdit={props.canEdit}
          saving={props.savingLimit === true}
          defaultEditing={props.defaultEditingLimit === true}
          onSave={props.onSaveWeeklyLimit}
        />
      )}
    </section>
  );
}
